import api from './api';
import { fuzzyMatchTitle } from '../utils/fuzzyMatch';

const DESIGN_PAGE_LIMIT = 25;
const FOLDER_PAGE_LIMIT = 50;
const MAX_FOLDERS = 25;
const MAX_FOLDER_DEPTH = 4;
const MAX_PAGES_PER_FOLDER = 4;
const FOLDER_CONCURRENCY = 5;

// BFS over the folder tree, one level at a time, each level's fetches run
// concurrently. Bounded on both axes (MAX_FOLDERS, MAX_FOLDER_DEPTH) so a
// large or deep account can't turn a search into an unbounded crawl. Only
// the first FOLDER_PAGE_LIMIT subfolders of any single folder are seen —
// an accepted cap, not a full walk of each folder's own pagination.
async function discoverFolderIds(folderCache) {
  const discovered = ['root'];
  const seen = new Set(['root']);
  let frontier = ['root'];
  let depth = 0;

  while (frontier.length && discovered.length < MAX_FOLDERS && depth < MAX_FOLDER_DEPTH) {
    depth += 1;
    const levelResults = await Promise.all(frontier.map(async (folderId) => {
      const cached = folderCache.get(folderId);
      if (cached) return cached;
      try {
        const page = await api.fetchFolders({ folderId, limit: FOLDER_PAGE_LIMIT });
        folderCache.set(folderId, page.folders);
        return page.folders;
      } catch {
        return [];
      }
    }));

    const nextFrontier = [];
    for (const folders of levelResults) {
      for (const folder of folders) {
        if (seen.has(folder.folder_id) || discovered.length >= MAX_FOLDERS) continue;
        seen.add(folder.folder_id);
        discovered.push(folder.folder_id);
        nextFrontier.push(folder.folder_id);
      }
    }
    frontier = nextFrontier;
  }

  return discovered;
}

async function searchFolderDesigns(folderId, query, designPageCache, recordMatch) {
  let continuation = '';
  for (let page = 0; page < MAX_PAGES_PER_FOLDER; page += 1) {
    const cacheKey = `${folderId}:${continuation}`;
    let result = designPageCache.get(cacheKey);
    if (!result) {
      result = await api.fetchDesigns({ folderId, continuation, limit: DESIGN_PAGE_LIMIT });
      designPageCache.set(cacheKey, result);
    }

    result.designs
      .filter((design) => fuzzyMatchTitle(design.title, query))
      .forEach(recordMatch);

    if (!result.hasMore || !result.continuation) break;
    continuation = result.continuation;
  }
}

const NUMBER_QUERY = /^#?(\d+)$/;

/**
 * A query that is only a number (optionally "#114") ranks titles carrying
 * that number as a whole token ("Himno #114 - …") ahead of titles that merely
 * contain the digits. Stable for everything else.
 */
export function rankSearchMatches(matches, query) {
  const number = NUMBER_QUERY.exec(query.trim())?.[1];
  if (!number) return matches;
  const wholeToken = new RegExp(`(^|[^\\p{L}\\p{N}])#?${number}(?![\\p{L}\\p{N}])`, 'u');
  const exact = matches.filter((design) => wholeToken.test(design.title || ''));
  if (exact.length === 0) return matches;
  return [...exact, ...matches.filter((design) => !exact.includes(design))];
}

async function mapWithConcurrency(items, concurrency, fn) {
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      await fn(item);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
}

/**
 * Fuzzy-search design titles across a user's whole Canva account (root plus
 * a bounded crawl of subfolders) rather than just the currently-open folder.
 * Folder discovery and the per-folder design walk both run with bounded
 * concurrency so wall-clock stays roughly flat as folder count grows.
 * `folderCache`/`designPageCache` are caller-owned Maps (session-lived) so
 * repeated searches (e.g. as the user refines a query) skip network calls
 * for folders/pages already seen.
 *
 * @param {object} opts
 * @param {string} opts.query
 * @param {number} [opts.limit]
 * @param {Map} opts.folderCache
 * @param {Map} opts.designPageCache
 * @param {(matches: object[]) => void} [opts.onProgress] Called after each
 *   folder finishes with the matches found so far, capped at `limit` — lets
 *   the caller render results incrementally instead of waiting for the
 *   whole crawl.
 * @param {() => boolean} [opts.shouldStop] Polled between async steps so a
 *   superseded search (newer query, unmount) can abandon in-flight work.
 * @returns {Promise<object[]>}
 */
export async function searchDesignsAcrossAccount({
  query,
  limit = DESIGN_PAGE_LIMIT,
  folderCache,
  designPageCache,
  onProgress,
  shouldStop,
}) {
  const matches = [];
  let stopped = false;

  const recordMatch = (design) => {
    if (stopped) return;
    matches.push(design);
    if (matches.length >= limit) stopped = true;
  };

  const ranked = () => rankSearchMatches(matches.slice(0, limit), query);

  const folderIds = await discoverFolderIds(folderCache);
  if (shouldStop?.()) return ranked();

  await mapWithConcurrency(folderIds, FOLDER_CONCURRENCY, async (folderId) => {
    if (stopped || shouldStop?.()) return;
    await searchFolderDesigns(folderId, query, designPageCache, recordMatch);
    onProgress?.(ranked());
  });

  return ranked();
}
